import axios from 'axios';

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api';

export const apiClient = axios.create({
  baseURL,
  timeout: 10000,
});

// Inject MSAL token in real environments.
apiClient.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---- types ----
export interface Vulnerability {
  severity: 'Critical' | 'High' | 'Medium' | 'Low' | 'Informational';
  finding: string;
  assetName: string;
  status: 'Open' | 'InProgress' | 'Mitigated' | 'Resolved' | 'Accepted';
}

export interface DashboardOverview {
  securityScore: number;
  scoreTrendDays30: number;
  vulnerabilities: Vulnerability[];
  rbac: {
    totalUsers: number;
    roles: number;
    mfaCoveragePct: number;
    criticalAccessViolations: number;
  };
  globalCompliancePct: number;
}

export interface ResidencyRow {
  country: string;
  countryName: string;
  isCompliant: boolean;
  assetCount: number;
  note?: string;
}

export interface ComplianceCard {
  frameworkCode: string;
  frameworkName: string;
  description: string;
  lastGeneratedAt?: string;
  status: string;
  openViolations: number;
  downloadUrl?: string;
}

export interface PiaItem {
  id: string;
  modelOrFeatureName: string;
  status: 'Planned' | 'InReview' | 'Completed' | 'Overdue';
  riskScore: number;
  reviewer: { reviewerName: string; reviewerEmail: string };
  dueDate: string;
}

export interface PlanInfo {
  name: string;
  planId: string;
  displayName: string;
  maxGpuCount: number;
  seats: number;
  monthlyPricePerSeat: number;
  allFrameworks: boolean;
  status: string;
}

export interface MeInfo {
  email: string;
  displayName: string;
  role: string;
  mfaEnabled: boolean;
}

// ---- API methods ----
export const api = {
  dashboardOverview: () => apiClient.get<DashboardOverview>('/dashboard/overview').then(r => r.data),
  currentPlan:       () => apiClient.get<PlanInfo>('/dashboard/plan').then(r => r.data),
  residencyMap:      () => apiClient.get<ResidencyRow[]>('/data-residency/map').then(r => r.data),
  complianceList:    () => apiClient.get<ComplianceCard[]>('/compliance/reports').then(r => r.data),
  generateReport:    (code: string) => apiClient.post(`/compliance/reports/${code}/generate`).then(r => r.data),
  downloadReport:    (code: string) => apiClient
    .get(`/compliance/reports/${code}/download`, { responseType: 'blob' })
    .then(r => r.data as Blob),
  piaList:           () => apiClient.get<PiaItem[]>('/pia').then(r => r.data),
  me:                () => apiClient.get<MeInfo>('/me').then(r => r.data),
};
